import React, { useState } from 'react';
import {
  Globe, Server, Calendar, RefreshCw, Key, ExternalLink,
  Copy, Check, Edit2, Trash2, ShieldCheck, ShieldAlert, AlertCircle
} from 'lucide-react';

const WhatsAppIcon = ({ size = 16, style = {} }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="currentColor"
    viewBox="0 0 24 24"
    style={style}
  >
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.513 2.262 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.73-1.458L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.625 1.451 5.432.002 9.851-4.414 9.855-9.843.002-2.63-1.023-5.101-2.886-6.968-1.863-1.867-4.347-2.894-6.98-2.896-5.433 0-9.854 4.417-9.858 9.848-.002 1.81.488 3.579 1.419 5.141L1.896 22.15l6.3-1.652zM17.07 14.67c-.276-.138-1.636-.807-1.89-.899-.253-.093-.437-.138-.621.138-.184.276-.713.899-.874 1.084-.16.184-.321.207-.597.069-.276-.138-1.168-.43-2.226-1.374-.823-.733-1.378-1.64-1.539-1.916-.16-.276-.017-.425.12-.562.124-.124.276-.322.414-.483.138-.161.184-.276.276-.46.092-.184.046-.345-.023-.483-.069-.138-.621-1.496-.85-2.05-.224-.54-.449-.465-.621-.474-.16-.008-.344-.01-.528-.01-.184 0-.483.069-.736.345-.253.276-.966.943-.966 2.3 0 1.357.989 2.668 1.127 2.852.138.184 1.947 2.972 4.717 4.164.659.284 1.173.453 1.574.58.662.21 1.265.18 1.741.11.531-.078 1.636-.669 1.868-1.313.23-.643.23-1.196.16-1.312-.069-.115-.253-.184-.53-.322z" />
  </svg>
);

export default function DomainCard({ domain, onSelect, onEdit, onDelete, onRenew }) {
  const [copied, setCopied] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(domain.domainName);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRenew = async (e) => {
    e.stopPropagation();
    setIsRenewing(true);
    await onRenew(domain.id);
    setIsRenewing(false);
  };

  const getStatusBadge = () => {
    switch (domain.computedStatus) {
      case 'expired':
        return (
          <span className="badge badge-expired">
            <AlertCircle size={12} /> Expired
          </span>
        );
      case 'urgent':
        return (
          <span className="badge badge-urgent">
            <ShieldAlert size={12} /> Expiration Critical
          </span>
        );
      case 'expiring_soon':
        return (
          <span className="badge badge-expiring_soon">
            <ShieldAlert size={12} /> Expiring Soon
          </span>
        );
      default:
        return (
          <span className="badge badge-active">
            <ShieldCheck size={12} /> Active
          </span>
        );
    }
  };

  const days = domain.daysRemaining;

  const getCountdownColor = () => {
    if (days <= 0) return '#dc2626';
    if (days <= 7) return '#ea580c';
    if (days <= 30) return '#d97706';
    return '#059669';
  };

  const getAccentGradient = () => {
    if (days <= 0) return 'linear-gradient(90deg, #f43f5e 0%, #e11d48 100%)';
    if (days <= 7) return 'linear-gradient(90deg, #f97316 0%, #ea580c 100%)';
    if (days <= 30) return 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)';
    return 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)';
  };

  return (
    <div
      className="glass-panel glass-panel-interactive"
      style={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        justify: 'space-between',
        position: 'relative',
        background: '#ffffff',
        borderRadius: '22px',
        overflow: 'hidden'
      }}
      onClick={() => onSelect(domain)}
    >
      {/* Top Accent Strip */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: getAccentGradient()
      }} />

      {/* Top Section */}
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#0f172a', wordBreak: 'break-all', letterSpacing: '-0.3px' }}>
                {domain.domainName}
              </h3>
              <button
                onClick={handleCopy}
                title="Copy Domain Name"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: '2px' }}
              >
                {copied ? <Check size={15} color="#059669" /> : <Copy size={15} />}
              </button>
              <a
                href={`${domain.domainName}`}
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}
                title="Open domain website"
                style={{ color: 'var(--text-dim)' }}
              >
                <ExternalLink size={15} />
              </a>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
              {domain.category && (
                <span style={{
                  fontSize: '0.74rem',
                  color: '#4338ca',
                  background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                  padding: '3px 10px',
                  borderRadius: '8px',
                  fontWeight: '700',
                  border: '1px solid #c7d2fe'
                }}>
                  {domain.category}
                </span>
              )}
              {domain.clientName && (
                <span style={{
                  fontSize: '0.74rem',
                  color: '#15803d',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  padding: '3px 10px',
                  borderRadius: '8px',
                  fontWeight: '700',
                  border: '1px solid #bbf7d0'
                }}>
                  Client: {domain.clientName}
                </span>
              )}
              {domain.madeBy && (
                <span style={{
                  fontSize: '0.74rem',
                  color: '#7c3aed',
                  background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                  padding: '3px 10px',
                  borderRadius: '8px',
                  fontWeight: '700',
                  border: '1px solid #ddd6fe'
                }}>
                  Developer: {domain.madeBy}
                </span>
              )}
            </div>
          </div>
          {getStatusBadge()}
        </div>

        {/* Days Countdown Banner */}
        <div style={{
          background: days <= 0 ? '#fef2f2' : days <= 7 ? '#fff7ed' : '#f8fafc',
          border: `1px solid ${days <= 0 ? '#fecaca' : days <= 7 ? '#ffedd5' : '#e2e8f0'}`,
          borderRadius: '12px',
          padding: '10px 14px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: '600' }}>
            {days <= 0 ? 'Status Alert:' : 'Renewal Countdown:'}
          </span>
          <span style={{ fontSize: '0.92rem', fontWeight: '800', color: getCountdownColor() }}>
            {days <= 0
              ? `Expired ${Math.abs(days)} day(s) ago`
              : `${days} day(s) left`}
          </span>
        </div>

        {/* Platform & Hosting Details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px', fontSize: '0.82rem' }}>
          <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', padding: '10px 12px', borderRadius: '12px' }}>
            <div style={{ color: '#4338ca', fontSize: '0.72rem', fontWeight: '700', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Globe size={12} /> Registrar Platform
            </div>
            <div style={{ fontWeight: '700', color: '#1e1b4b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {domain.registrar || 'N/A'}
            </div>
          </div>

          <div style={{ background: '#ecfeff', border: '1px solid #cff4fc', padding: '10px 12px', borderRadius: '12px' }}>
            <div style={{ color: '#0891b2', fontSize: '0.72rem', fontWeight: '700', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Server size={12} /> Hosting Platform
            </div>
            <div style={{ fontWeight: '700', color: '#164e63', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {domain.hostingProvider || 'N/A'}
            </div>
          </div>
        </div>

        {/* Made By Website Developer */}
        {domain.madeBy && (
          <div style={{
            background: '#faf5ff',
            border: '1px solid #e9d5ff',
            padding: '10px 12px',
            borderRadius: '12px',
            marginBottom: '16px',
            fontSize: '0.82rem'
          }}>
            <div style={{ color: '#7c3aed', fontSize: '0.72rem', fontWeight: '700', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              🛠️ Website Created By
            </div>
            <div style={{ fontWeight: '700', color: '#581c87' }}>
              {domain.madeBy}
            </div>
          </div>
        )}

        {/* Pricing & Expiry */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={15} color="#6366f1" />
            <span>Renew: <strong style={{ color: '#0f172a' }}>{domain.renewalDate}</strong></span>
          </div>
          <div>
            <strong style={{ color: '#059669', fontSize: '1rem', fontWeight: '800' }}>₹{domain.cost || 0}</strong>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>/{domain.billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons Footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '14px', borderTop: '1px solid #f1f5f9' }}>
        {(days <= 30) ? (
          <button
            className="btn btn-renew"
            onClick={handleRenew}
            disabled={isRenewing}
            style={{ flex: 1, padding: '9px 14px', fontSize: '0.85rem' }}
          >
            <RefreshCw size={14} className={isRenewing ? 'spin' : ''} />
            <span>{isRenewing ? 'Renewing...' : 'Renew (+1 yr)'}</span>
          </button>
        ) : (
          <button
            className="btn btn-secondary"
            onClick={(e) => { e.stopPropagation(); onSelect(domain); }}
            style={{ flex: 1, padding: '9px 14px', fontSize: '0.85rem' }}
          >
            <Key size={14} />
            <span>View Details</span>
          </button>
        )}

        {domain.whatsappNo && (
          <a
            href={`https://wa.me/${domain.whatsappNo.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi, regarding your domain ${domain.domainName}...`)}`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-secondary btn-icon"
            style={{ borderColor: '#25d366', color: '#25d366', background: '#f0fdf4' }}
            title={`Chat with ${domain.clientName || 'Client'} on WhatsApp`}
            onClick={(e) => e.stopPropagation()}
          >
            <WhatsAppIcon size={16} />
          </a>
        )}

        <button
          className="btn btn-secondary btn-icon"
          onClick={(e) => { e.stopPropagation(); onEdit(domain); }}
          title="Edit Details"
        >
          <Edit2 size={16} /> 
        </button>

        <button
          className="btn btn-danger btn-icon"
          onClick={(e) => { e.stopPropagation(); onDelete(domain.id); }}
          title="Delete Domain"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}
